import { createStyles, makeStyles } from "@material-ui/styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Label,
    RelodeApiError,
    RelodeTheme,
    SelectableButtonItem,
    SelectableButtonsList,
    SelectInput,
    Text,
    TextArea,
    useNotifications,
} from "@relode/core";
import { JobStatusReasonType, JobStatus } from "@relode/core/domain";
import * as React from "react";

import {
    ChangeJobStatusDialogProps,
    ChangeJobStatusModalState,
    ChangeJobStatusModalStatus,
} from "./changeJobStatusDialogProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            field: {
                margin: theme.spacing(0),
            },
            section: {
                display: "flex",
                flexGrow: 1,
                flexDirection: "row",
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(5),
                [theme.breakpoints.up("md")]: {
                    marginBottom: theme.spacing(2),
                    flexDirection: "column",
                },
            },
            selectableButtonsList: {
                width: "100%",
                paddingTop: "6px",
                paddingBottom: "7px",
            },
            selectInput: {
                margin: theme.spacing(2, 0, 1, 0),
            },
            closeButtons: {
                width: "100%",
            },
        }),
    { name: "ChangeJobStatusDialog" }
);

export const ChangeJobStatusDialog = (props: ChangeJobStatusDialogProps) => {
    const classes = useStyles({});
    const { actions: notifications } = useNotifications();
    const {
        nextStatus,
        onClose,
        onJobStatusChange,
        onComplete,
        jobName,
    } = props;

    const [commentText, setCommentText] = React.useState<string>(undefined);
    const [commentError, setCommentError] = React.useState<string>(undefined);
    const [
        jobStatusChangedReasonId,
        setJobStatusChangedReasonId,
    ] = React.useState<number>(undefined);
    const [reasonError, setReasonError] = React.useState<string>(undefined);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState<boolean>(
        true
    );
    const isNextStatusClosed = nextStatus === JobStatus.Closed;
    const isNextStatusPaused = nextStatus === JobStatus.Paused;

    const [state, setState] = React.useState<ChangeJobStatusModalState>({
        error: undefined,
        status: "unset",
        isBusy: false,
    });

    React.useEffect(() => {
        if (commentText && commentText.trim().length > 0) {
            if (!isNextStatusClosed && !isNextStatusPaused) {
                setIsButtonDisabled(false);
            } else if (jobStatusChangedReasonId) {
                setIsButtonDisabled(false);
            }
        } else {
            setIsButtonDisabled(true);
        }
    }, [
        isNextStatusClosed,
        isNextStatusPaused,
        commentText,
        jobStatusChangedReasonId,
    ]);

    React.useEffect(() => {
        switch (state.status) {
            // NOTE: DO NOT SET ANY STATE HERE. WILL CAUSE LOOP
            case "pending": {
                notifications.closeAreaNotification("changeJobStatusError");
                break;
            }

            case "failure": {
                if (!state.error) return;

                const { validationErrors, summary } = state.error;

                if (validationErrors) {
                    validationErrors.forEach(ve => {
                        if (ve.field === "JobStatusReasonTypeType") {
                            setReasonError(ve.messages.join(" "));
                        }
                    });
                }
                if (summary) {
                    notifications.showArea(
                        "error",
                        summary,
                        "changeJobStatusError",
                        { canClose: false }
                    );
                }
                break;
            }

            case "success": {
                onComplete();
                break;
            }

            default: {
                notifications.closeAreaNotification("changeJobStatusError");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const setChangeJobStatusStatus = (
        status: ChangeJobStatusModalStatus,
        error: RelodeApiError | undefined
    ) => {
        setState(o => ({ ...o, status, error, isBusy: status === "pending" }));
    };

    const closeReasons: JobStatusReasonType[] = [
        JobStatusReasonType.ClosedRelodeHire,
        JobStatusReasonType.ClosedNotNeeded,
        JobStatusReasonType.ClosedOutsideHire,
    ];

    const pauseReasons: JobStatusReasonType[] = [
        JobStatusReasonType.PausedOfferNegotiation,
        JobStatusReasonType.PausedReconsideringNeed,
        JobStatusReasonType.PausedRequalifying,
    ];

    const handleCommentChange = (event: React.ChangeEvent<any>) => {
        setCommentText(event.target.value);
        setCommentError(undefined);
    };

    const handleSelectableButtonListInputChange = (
        value: SelectableButtonItem
    ) => {
        setReasonError(undefined);
        setJobStatusChangedReasonId(value.id);
    };

    const handleSelectInputChange = (
        event: any,
        value: JobStatusReasonType
    ) => {
        setReasonError(undefined);
        setJobStatusChangedReasonId(value.reasonId);
    };

    const handleConfirm = async () => {
        let isValid = true;
        if (!commentText || commentText.trim().length <= 0) {
            setCommentError("please add a comment");
            isValid = false;
        }

        if (
            !jobStatusChangedReasonId &&
            (isNextStatusClosed || isNextStatusPaused)
        ) {
            setReasonError("please select a reason");
            isValid = false;
        }

        if (isValid) {
            setChangeJobStatusStatus("pending", undefined);
            await onJobStatusChange(
                nextStatus,
                commentText,
                jobStatusChangedReasonId,
                setChangeJobStatusStatus
            );
        }
    };

    return (
        <Dialog open onClose={onClose}>
            <DialogTitle
                title={`${nextStatus.actionLabel}`}
                onClose={onClose}
            />
            <DialogContent notificationAreaName="changeJobStatusError">
                <Text dataCy="changeJobAStatusJobName" variant="h4">
                    {jobName}
                </Text>
                {isNextStatusClosed ? (
                    <div className={classes.section}>
                        <Label
                            label="did you make a hire?"
                            className={classes.closeButtons}
                            fullWidth
                        >
                            <SelectableButtonsList
                                id="JobStatusReasonType"
                                className={classes.selectableButtonsList}
                                onClick={handleSelectableButtonListInputChange}
                                items={closeReasons.map(item => ({
                                    id: item.reasonId,
                                    label: item.clientDescription,
                                    selected:
                                        jobStatusChangedReasonId ===
                                        item.reasonId,
                                }))}
                                size="fullWidth"
                                error={!!reasonError}
                                helperText={reasonError}
                            />
                        </Label>
                    </div>
                ) : isNextStatusPaused ? (
                    <SelectInput
                        label="* status reason"
                        mode="list"
                        options={pauseReasons}
                        getOptionLabel={o => o.clientDescription}
                        onChange={handleSelectInputChange}
                        disableTyping
                        error={!!reasonError}
                        helperText={reasonError}
                        className={classes.selectInput}
                        data-cy={"statusReasonSelect"}
                    />
                ) : null}
                <TextArea
                    data-cy="changeJobStatusComment"
                    id="comment"
                    className={classes.field}
                    inputProps={{ maxLength: 300 }}
                    label="add a comment"
                    name="comment"
                    value={commentText}
                    showCharacterLimit
                    multiline={true}
                    rows={5}
                    onChange={handleCommentChange}
                    helperText={commentError}
                    error={!!commentError}
                />
            </DialogContent>
            <DialogActions
                submit={{
                    label: nextStatus.actionLabel,
                    disabled: isButtonDisabled || state.isBusy,
                    onSubmit: handleConfirm,
                }}
            />
        </Dialog>
    );
};
